/**
 * iPhone install guide: Web Push on iOS works only for a web app added to the Home Screen (iOS 16.4+),
 * and the installed app keeps its own storage — so pairing happens inside the installed app.
 */
import { HopexEmblem } from '@hopex/ui';
import { BellRing, PlusSquare, Share, Smartphone } from 'lucide-react';

const STEPS = [
  {
    icon: Share,
    title: 'دکمه‌ی اشتراک‌گذاری',
    text: 'در پایین سافاری روی آیکون مربع با فلش رو به بالا بزنید.',
  },
  {
    icon: PlusSquare,
    title: '«Add to Home Screen»',
    text: 'در فهرست باز شده گزینه‌ی «افزودن به صفحه‌ی اصلی» را انتخاب کنید.',
  },
  {
    icon: Smartphone,
    title: 'باز کردن از صفحه‌ی اصلی',
    text: 'برنامه‌ی «پشتیبانی هوپکس» را از صفحه‌ی اصلی گوشی باز کنید.',
  },
  {
    icon: BellRing,
    title: 'اتصال و اعلان',
    text: 'کد اتصال را وارد کنید و اعلان‌ها را روشن کنید.',
  },
];

export function InstallGuide({ onContinue }: { onContinue: () => void }) {
  return (
    <main
      className="hx-ambient flex min-h-dvh flex-col bg-canvas px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-[calc(env(safe-area-inset-top)+28px)] text-fg"
      data-desk-install
    >
      <div className="mx-auto flex w-full max-w-[420px] flex-1 flex-col gap-6">
        <header className="dk-enter flex flex-col items-center gap-3 text-center">
          <span className="grid size-16 place-items-center rounded-[22px] bg-canvas-deep shadow-float hx-frame">
            <HopexEmblem size={32} decorative />
          </span>
          <h1 className="m-0 text-[22px] font-extrabold leading-9">نصب میز پشتیبانی روی آیفون</h1>
          <p className="m-0 text-[13.5px] leading-7 text-fg-muted">
            برای دریافت اعلان گفتگوهای تازه، برنامه را یک بار به صفحه‌ی اصلی اضافه کنید. فقط چند
            ثانیه طول می‌کشد.
          </p>
        </header>
        <ol className="dk-stagger m-0 flex list-none flex-col gap-2.5 p-0">
          {STEPS.map((s, i) => (
            <li
              key={s.title}
              className="hx-card flex items-start gap-3 rounded-[20px] border border-line bg-surface-1 p-4"
            >
              <span className="relative grid size-11 shrink-0 place-items-center rounded-full bg-accent-tint text-accent-text">
                <s.icon aria-hidden className="size-5" />
                <span className="hx-num absolute -end-1 -top-1 grid size-5 place-items-center rounded-full bg-accent text-[11px] font-bold text-on-accent">
                  {(i + 1).toLocaleString('fa-IR')}
                </span>
              </span>
              <div className="min-w-0">
                <p className="m-0 text-[14px] font-bold">{s.title}</p>
                <p className="m-0 mt-0.5 text-[12.5px] leading-6 text-fg-muted">{s.text}</p>
              </div>
            </li>
          ))}
        </ol>
        <p className="m-0 rounded-[16px] border border-dashed border-line px-4 py-3 text-[12px] leading-6 text-fg-subtle">
          اعلان روی آیفون به iOS نسخه‌ی ۱۶٫۴ یا بالاتر نیاز دارد. در کامپیوتر یا اندروید نصب لازم
          نیست.
        </p>
        <div className="mt-auto flex flex-col gap-2">
          <button
            type="button"
            onClick={onContinue}
            className="min-h-11 cursor-pointer rounded-full border border-line-strong bg-surface-1 px-5 text-[13.5px] font-semibold text-fg-muted hover:text-fg"
            data-install-skip
          >
            فعلاً در همین مرورگر ادامه می‌دهم
          </button>
        </div>
      </div>
    </main>
  );
}
